/**
 * @file noteFormat.ts
 * @brief Wording and formats the web Notes pages share with the phone.
 *
 * @details
 * Same strings and order as `FRONTEND/src/screens/Notes/NotesListScreen.tsx`:
 * `formatWhen` ("13 Sep 15:42"), MM:SS lengths, the "Manual / Generated"
 * kind line, the row preview, the meeting label and the share text. Kept
 * apart from `noteUi.tsx` so that file exports only components (fast refresh).
 *
 * @author DES
 * @date 30-09-2026
 */

import { format } from "date-fns";
import { notePointLevels, notePoints, noteSummary, type VoiceNote } from "./schemas";
import { formatPointsText } from "./pointLevels";
import { dockGlass } from "@/shared/theme/dockGlass";

/**
 * The phone's `formatWhen` as it renders on an iPhone: "30 Sep at 16:13".
 * Mobile calls `toLocaleString` (day 2-digit, short month, time), and iOS
 * inserts "at"; a browser renders the same call differently, so the web
 * spells out what the phone shows (checked on the simulator, 30-09-2026).
 */
export function formatWhen(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, "dd MMM 'at' HH:mm");
}

/** MM:SS, zero-padded, as the phone shows a note's length. */
export function formatNoteDuration(seconds?: number | string | null): string {
  const total = Math.max(0, Math.floor(Number(seconds ?? 0)) || 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function noteStatusLabel(note: VoiceNote): string {
  switch (note.status) {
    case "ready":
      return "Ready";
    case "processing":
      return "Transcribing…";
    case "analyzing":
      return "Analyzing…";
    case "error":
      return "Failed";
    default:
      return "Processing";
  }
}

/** "Manual · 3 points" / "Generated · 02:14 · Ready" — the row's kind line. */
export function noteKindLine(note: VoiceNote): string {
  if (note.source === "typed") {
    const n = notePoints(note).length;
    return `Manual · ${n} point${n === 1 ? "" : "s"}`;
  }
  return `Generated · ${formatNoteDuration(note.duration_seconds)} · ${noteStatusLabel(note)}`;
}

/** What a row says under its meta: points, else summary, else transcript. */
export function notePreview(note: VoiceNote): string {
  const points = notePoints(note);
  if (points.length > 0) return points.join("  ·  ");
  const summary = noteSummary(note);
  if (summary) return summary;
  if (note.status === "ready" && note.transcript_text) return note.transcript_text;
  if (note.status === "processing") return "Transcribing…";
  if (note.status === "analyzing") return "Reading your note…";
  if (note.status === "error") return "Failed";
  return "Voice note";
}

/**
 * Every link is a before-the-meeting one: the note is an agenda, and wears
 * Agenda's flag instead of the calendar (DES 08-10-2026, as the phone's NoteRow).
 */
export function noteIsAgenda(note: VoiceNote): boolean {
  const links = note.meeting_links ?? [];
  return links.length > 0 && links.every((l) => l.role !== "addendum");
}

/**
 * The ROLE leads: before the meeting the note is its agenda and shapes its
 * intelligence; after, it is filed with it. Mixed links get neither word.
 */
export function noteMeetingLabel(note: VoiceNote): string | null {
  const links = note.meeting_links ?? [];
  if (links.length === 0) return null;
  const allPrep = links.every((l) => l.role !== "addendum");
  const allAfter = links.every((l) => l.role === "addendum");
  const role = allPrep ? "Agenda" : allAfter ? "Added after" : "Attached";
  const what =
    links.length === 1
      ? links[0].title?.trim() || "Untitled meeting"
      : `${links.length} meetings`;
  return `${role} · ${what}`;
}

/** The text a note is shared as, in the phone's order. */
export function noteShareText(note: VoiceNote): string {
  const parts = [note.title?.trim() || "Untitled note"];
  const summary = noteSummary(note);
  if (summary) parts.push(summary);
  const points = notePoints(note);
  // Sub-points (DES 12-10-2026): indented four spaces, lettered under their point.
  if (points.length) parts.push(formatPointsText(points, notePointLevels(note, points)));
  if (note.source !== "typed" && note.transcript_text?.trim()) {
    parts.push(`Transcript:\n${note.transcript_text.trim()}`);
  }
  return parts.join("\n\n");
}

/**
 * The frosted pill every Notes dock sits on — the web `VZActionDock` glass.
 * 56px tall like `VZ_CONTROL_HEIGHT`, fully round like every pill.
 * The look lives in shared/theme/dockGlass.ts (04-10-2026).
 */
export const noteDockGlass = dockGlass;

/**
 * Shown when a note's meeting report has nothing spoken to match against
 * (server code NO_TRANSCRIPT). A fact, not a failure the user can retry: the
 * first Meeting Notes test said "no transcript. Try again" (DES 06-10-2026,
 * the phone's 03806ae).
 */
export const NOTHING_SAID_MESSAGE =
  "Nothing was said in this recording, so there is nothing to match your notes against.";

/** The server's report failure text, with "no transcript" made readable. */
export function noteReportErrorText(message?: string | null, code?: string | null): string {
  const m = String(message ?? "").trim();
  if (code === "NO_TRANSCRIPT" || /no transcript/i.test(m)) return NOTHING_SAID_MESSAGE;
  return m || "Could not prepare the report.";
}
